import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Flex,
  Text,
  SimpleGrid,
  Grid,
  Spinner,
  Badge,
  IconButton,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  Tooltip,
  useToast,
} from '@chakra-ui/react';
import {
  CaretLeft,
  CaretRight,
  CaretUp,
  CaretDown,
  Receipt,
  Buildings,
  ArrowUDownLeft,
  XCircle,
  CheckCircle,
  WarningCircle,
  MinusCircle,
  MagnifyingGlass,
  FunnelSimple,
  Copy,
  FileXls,
} from 'phosphor-react';
import { GlassPageBg } from '../../../../components/glass/GlassPageBg';
import { GlassCard } from '../../../../components/glass/GlassCard';
import {
  useConferenciaTotais,
  useConferenciaSerie,
  useConferenciaEmpresas,
  useConferenciaDuplicadas,
  IDuplicadasResumo,
  IEmpresaCard,
  IContagemTipos,
  IFonteContagem,
  IDiffCelula,
  IConferenciaKpis,
  ISerieMes,
  StatusConf,
  AutmaisStatus,
  EmpresaBuscaModo,
  OrdemEmpresas,
  IFiltrosAvancados,
  SentidoNota,
  TipoDoc,
} from '../../../../hooks/useConferenciaFiscal';
import { FiltrosAvancados } from './FiltrosAvancados';
import { iniciarExportacaoConferencia } from './exportacao';
import { PARAM_BAIXAR, baixarComAviso } from './notificacoes';
import {
  InfoTip,
  MultiSparkline,
  TriagemChip,
  ITriagemChip,
  computeDelta,
  deltaColorFor,
} from './kpiBits';
import { NotasDrawer } from './NotasDrawer';
import { AutmaisLogDrawer } from './AutmaisLogDrawer';
import { DuplicadasDrawer } from './DuplicadasDrawer';

const MESES = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

const fmtInt = (n: number) => n.toLocaleString('pt-BR');

/** "14:30" se foi hoje, "21/10 14:30" se não — o horário do confronto em cache. */
const horarioDoCalculo = (iso: string | undefined, agora = new Date()): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const hora = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  return d.toDateString() === agora.toDateString()
    ? hora
    : `${d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })} ${hora}`;
};

// Valor compacto (1,2 mi / 340 mil / 1.250) — o card não comporta o número cheio
// e a ordem de grandeza é o que importa pra priorizar a fila de trabalho.
const fmtValor = (n: number): string => {
  const v = Number.isFinite(n) ? n : 0;
  // A faixa é escolhida pelo valor JÁ ARREDONDADO: decidindo antes, 999.999
  // vira "1.000 mil" — que se lê como 1 milhão, mil vezes o valor real, e
  // inverteria a priorização da fila que este número existe pra ordenar.
  const mil = Math.round(v / 1_000);
  if (Math.abs(mil) >= 1_000)
    return `${(v / 1_000_000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} mi`;
  if (Math.abs(mil) >= 1) return `${mil.toLocaleString('pt-BR')} mil`;
  return v.toLocaleString('pt-BR', { maximumFractionDigits: 0 });
};

// ---- filtros avançados: URL <-> estado <-> query da API ----
const SENTIDOS = ['entrada', 'saida'] as const;
const DOCS = ['nfe', 'nfce', 'cte', 'nfse'] as const;

/** Lê os filtros avançados da URL (params CSV, iguais aos do backend). */
function lerAvancados(p: URLSearchParams): IFiltrosAvancados {
  const csv = (k: string, permitidos?: readonly string[]) =>
    (p.get(k) || '')
      .split(',')
      .map(s => s.trim())
      .filter(s => s && (!permitidos || permitidos.includes(s)));
  const numero = (k: string) => {
    const n = Number(p.get(k));
    return p.get(k) && Number.isFinite(n) ? n : undefined;
  };
  const sent = p.get('sent');
  const f: IFiltrosAvancados = {
    acionaveis: p.get('acion') === '1' || undefined,
    sit_div: p.get('sd') === '1' || undefined,
    portal_cego: p.get('pc') === '1' || undefined,
    r_min: numero('rmin'),
    r_max: numero('rmax'),
    sentido: SENTIDOS.includes(sent as never) ? (sent as SentidoNota) : undefined,
    doc: csv('doc', DOCS) as TipoDoc[],
    uf: csv('uf'),
  };
  if (!f.doc?.length) delete f.doc;
  if (!f.uf?.length) delete f.uf;
  return f;
}

/** Espelha os avançados na URL (ausência = sem filtro; nunca serializa default). */
function escreveAvancados(p: URLSearchParams, f: IFiltrosAvancados) {
  if (f.acionaveis) p.set('acion', '1');
  if (f.sit_div) p.set('sd', '1');
  if (f.portal_cego) p.set('pc', '1');
  if (f.r_min != null) p.set('rmin', String(f.r_min));
  if (f.r_max != null) p.set('rmax', String(f.r_max));
  if (f.sentido) p.set('sent', f.sentido);
  if (f.doc?.length) p.set('doc', f.doc.join(','));
  if (f.uf?.length) p.set('uf', f.uf.join(','));
}

/** Monta os params da chamada à API (arrays viram CSV). */
function avancadosParaQuery(f: IFiltrosAvancados) {
  return {
    acionaveis: f.acionaveis || undefined,
    sit_div: f.sit_div || undefined,
    portal_cego: f.portal_cego || undefined,
    r_min: f.r_min,
    r_max: f.r_max,
    sentido: f.sentido,
    doc: f.doc?.length ? f.doc.join(',') : undefined,
    uf: f.uf?.length ? f.uf.join(',') : undefined,
  };
}

/** Descreve cada filtro avançado ativo como chip removível (label + estado sem ele). */
function descreveAvancados(
  f: IFiltrosAvancados,
): Array<{ k: string; lab: string; limpa: IFiltrosAvancados }> {
  const out: Array<{ k: string; lab: string; limpa: IFiltrosAvancados }> = [];
  const fmtMil = (n?: number) => {
    if (n == null) return '';
    return n >= 1000 ? `${Math.round(n / 1000)} mil` : String(n);
  };
  const faixaLab = () => {
    if (f.r_min != null && f.r_max != null) return `R$ ${fmtMil(f.r_min)}–${fmtMil(f.r_max)}`;
    if (f.r_min != null) return `R$ > ${fmtMil(f.r_min)}`;
    return `R$ até ${fmtMil(f.r_max)}`;
  };
  if (f.acionaveis)
    out.push({ k: 'acion', lab: 'Só acionáveis', limpa: { ...f, acionaveis: undefined } });
  if (f.sit_div)
    out.push({ k: 'sd', lab: 'Situação divergente', limpa: { ...f, sit_div: undefined } });
  if (f.portal_cego)
    out.push({ k: 'pc', lab: 'Portal viu, robô não', limpa: { ...f, portal_cego: undefined } });
  if (f.sentido)
    out.push({
      k: 'sent',
      lab: f.sentido === 'entrada' ? 'Entrada' : 'Saída',
      limpa: { ...f, sentido: undefined },
    });
  if (f.r_min != null || f.r_max != null) {
    out.push({ k: 'rs', lab: faixaLab(), limpa: { ...f, r_min: undefined, r_max: undefined } });
  }
  if (f.doc?.length)
    out.push({
      k: 'doc',
      lab: f.doc.map(d => d.toUpperCase()).join('/'),
      limpa: { ...f, doc: undefined },
    });
  if (f.uf?.length)
    out.push({ k: 'uf', lab: `UF: ${f.uf.join(',')}`, limpa: { ...f, uf: undefined } });
  return out;
}

/** Quantos filtros avançados estão ativos (pro botão "Filtros (N)"). */
function contaAvancados(f: IFiltrosAvancados): number {
  let n = 0;
  if (f.acionaveis) n += 1;
  if (f.sit_div) n += 1;
  if (f.portal_cego) n += 1;
  if (f.r_min != null || f.r_max != null) n += 1;
  if (f.sentido) n += 1;
  if (f.doc?.length) n += 1;
  if (f.uf?.length) n += 1;
  return n;
}

const rangeDoMes = (ano: number, mes: number) => {
  const ini = new Date(ano, mes, 1);
  const fim = new Date(ano, mes + 1, 0);
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  return { data_ini: iso(ini), data_fim: iso(fim) };
};

// -------------------------------------------------------------------
// KPI combinado "Canceladas por fonte" — barras comparativas Domínio/Autmais/
// Portal (divergência entre fontes é sinal). Estilo do card EMPRESAS do
// conciliacao-contabil (bolinha + rótulo + barra proporcional + valor na cor).
// -------------------------------------------------------------------
const FONTE_COR = { dom: '#1B88F5', aut: '#b45309', por: '#6d28d9' };

interface FonteMetrica {
  label: string;
  valor: number;
  cor: string;
  serie: number[];
}

const FonteLinha = ({
  m,
  max,
  positiveWhenDown,
  fmt,
}: {
  m: FonteMetrica;
  max: number;
  positiveWhenDown?: boolean;
  fmt: (n: number) => string;
}) => {
  const { label, valor, cor, serie } = m;
  const pct = max > 0 ? (valor / max) * 100 : 0;
  const delta = serie.length ? computeDelta(serie) : null;
  const dcor = deltaColorFor(delta, positiveWhenDown);
  return (
    <Flex align="center" gap={2}>
      <Box w="7px" h="7px" borderRadius="full" bg={cor} flexShrink={0} />
      <Text fontSize="11px" color="#64748B" fontWeight={500} w="52px" flexShrink={0}>
        {label}
      </Text>
      <Box
        flex={1}
        h="4px"
        borderRadius="full"
        bg="rgba(15,23,42,0.06)"
        overflow="hidden"
        minW="16px"
      >
        <Box h="100%" borderRadius="full" bg={cor} w={`${pct}%`} transition="width 0.5s ease" />
      </Box>
      <Text
        fontSize="13px"
        fontWeight={700}
        color={cor}
        w="56px"
        textAlign="right"
        flexShrink={0}
        sx={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {fmt(valor)}
      </Text>
      <Flex w="46px" justify="flex-end" flexShrink={0}>
        {delta != null && delta !== 0 && (
          <Flex
            align="center"
            gap={0.5}
            px={1}
            py="1px"
            borderRadius="md"
            bg={`${dcor}14`}
            color={dcor}
          >
            {delta < 0 ? <CaretDown size={9} weight="bold" /> : <CaretUp size={9} weight="bold" />}
            <Text fontSize="9px" fontWeight={700}>
              {Math.abs(delta).toLocaleString('pt-BR')}
            </Text>
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};

// Chip de sentido (entrada/saída) — clicável como filtro, toggle.
const SentidoChip = ({
  label,
  valor,
  cor,
  bg,
  ativo,
  onClick,
}: {
  label: string;
  valor: number;
  cor: string;
  bg: string;
  ativo?: boolean;
  onClick?: () => void;
}) => (
  <Flex
    as="button"
    type="button"
    onClick={onClick}
    align="center"
    gap={1.5}
    bg={ativo ? cor : bg}
    border="1px solid"
    borderColor={ativo ? cor : `${cor}22`}
    borderRadius="8px"
    px={2}
    py="3px"
    fontSize="11px"
    fontWeight={700}
    color={ativo ? 'white' : cor}
    cursor="pointer"
    transition="all 0.15s"
    _hover={{ filter: 'brightness(0.93)' }}
    sx={{ fontVariantNumeric: 'tabular-nums' }}
  >
    <Box w="6px" h="6px" borderRadius="full" bg={ativo ? 'white' : cor} flexShrink={0} />
    {valor.toLocaleString('pt-BR')} {label}
  </Flex>
);

// Toggle segmentado compacto (usado no card de Devolução: venda ↔ própria).
function MiniToggle<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T;
  onChange: (v: T) => void;
  options: Array<[T, string]>;
}) {
  return (
    <Flex bg="rgba(15,23,42,0.06)" borderRadius="md" p="2px" gap="2px">
      {options.map(([v, lbl]) => (
        <Box
          key={v}
          as="button"
          type="button"
          px={1.5}
          py="1px"
          borderRadius="sm"
          fontSize="9px"
          fontWeight={700}
          textTransform="none"
          color={value === v ? '#0F172A' : '#94A3B8'}
          bg={value === v ? 'white' : 'transparent'}
          boxShadow={value === v ? 'sm' : 'none'}
          onClick={() => onChange(v)}
        >
          {lbl}
        </Box>
      ))}
    </Flex>
  );
}

interface KpiPorFonteProps {
  titulo: string;
  icon: React.ReactNode;
  accent: string;
  info: string;
  fontes: FonteMetrica[];
  positiveWhenDown?: boolean;
  fmt?: (n: number) => string;
  titleToggle?: React.ReactNode;
}
const KpiPorFonte = ({
  titulo,
  icon,
  accent,
  info,
  fontes,
  positiveWhenDown,
  fmt = fmtInt,
  titleToggle,
}: KpiPorFonteProps) => {
  const max = Math.max(...fontes.map(f => f.valor), 1);
  return (
    <GlassCard intensity="strong" p={0} h="full" overflow="hidden">
      <Box h="3px" w="full" bg={accent} />
      <Flex direction="column" gap={2} h="full" p="13px 14px">
        <Flex align="center" justify="space-between" gap={2}>
          <Flex align="center" gap={1.5} minW={0}>
            <Box color={accent} display="flex">
              {icon}
            </Box>
            <Text
              fontSize="10px"
              fontWeight={600}
              color="#94A3B8"
              textTransform="uppercase"
              letterSpacing="0.08em"
            >
              {titulo}
            </Text>
            {titleToggle}
          </Flex>
          <Flex align="center" gap={2} flexShrink={0}>
            <MultiSparkline series={fontes.map(f => ({ data: f.serie, color: f.cor }))} />
            <InfoTip label={info} />
          </Flex>
        </Flex>
        <Flex direction="column" gap="7px" mt="auto">
          {fontes.map(f => (
            <FonteLinha
              key={f.label}
              m={f}
              max={max}
              positiveWhenDown={positiveWhenDown}
              fmt={fmt}
            />
          ))}
        </Flex>
      </Flex>
    </GlassCard>
  );
};

type SerieFn = (sel: (s: ISerieMes) => number) => number[];

// Card Empresas reformulado: quantas processadas por fonte (barras) + ativas /
// sem-dados no rodapé.
const KpiEmpresas = ({ k, serie }: { k?: IConferenciaKpis; serie: SerieFn }) => {
  const fontes: FonteMetrica[] = k
    ? [
        {
          label: 'Domínio',
          valor: k.empresas_dominio,
          cor: FONTE_COR.dom,
          serie: serie(s => s.empresas),
        },
        {
          label: 'Autmais',
          valor: k.empresas_autmais,
          cor: FONTE_COR.aut,
          serie: serie(s => s.empresas_autmais),
        },
        {
          label: 'Portal',
          valor: k.empresas_portal,
          cor: FONTE_COR.por,
          serie: serie(s => s.empresas_portal),
        },
      ]
    : [];
  const max = Math.max(...fontes.map(f => f.valor), 1);
  return (
    <GlassCard intensity="strong" p={0} h="full" overflow="hidden">
      <Box h="3px" w="full" bg="#059669" />
      <Flex direction="column" gap={2} h="full" p="13px 14px">
        <Flex align="center" justify="space-between" gap={2}>
          <Flex align="center" gap={1.5}>
            <Box color="#059669" display="flex">
              <Buildings size={16} weight="duotone" />
            </Box>
            <Text
              fontSize="10px"
              fontWeight={600}
              color="#94A3B8"
              textTransform="uppercase"
              letterSpacing="0.08em"
            >
              Empresas
            </Text>
          </Flex>
          <Flex align="center" gap={2} flexShrink={0}>
            <MultiSparkline series={fontes.map(f => ({ data: f.serie, color: f.cor }))} />
            <InfoTip label="Empresas ATIVAS no período processadas por cada fonte (com ≥1 nota). Uma fonte com poucas empresas indica cobertura baixa. A tag é a variação vs. o mês anterior. No rodapé: total de ativas e quantas ficaram sem nenhum dado." />
          </Flex>
        </Flex>
        <Flex direction="column" gap="7px" mt="auto">
          {fontes.map(f => (
            <FonteLinha key={f.label} m={f} max={max} fmt={fmtInt} />
          ))}
        </Flex>
        <Flex
          pt="8px"
          borderTop="1px solid rgba(15,23,42,0.08)"
          align="center"
          gap={1.5}
          fontSize="11.5px"
          fontWeight={600}
        >
          <Text color="#0F172A">{k ? fmtInt(k.empresas_ativas) : '—'}</Text>
          <Text color="#94A3B8" fontWeight={500}>
            ativas
          </Text>
          <Text color="#CBD5E1">·</Text>
          <Text color="#64748B">{k ? fmtInt(k.empresas_sem_dados) : '—'}</Text>
          <Text color="#94A3B8" fontWeight={500}>
            sem dados
          </Text>
        </Flex>
      </Flex>
    </GlassCard>
  );
};

// Card Devolução com toggle Venda (entrada) ↔ Própria (saída).
const KpiDevolucao = ({ k, serie }: { k?: IConferenciaKpis; serie: SerieFn }) => {
  const [modo, setModo] = useState<'venda' | 'propria'>('venda');
  const montaFontes = (): FonteMetrica[] => {
    if (!k) return [];
    if (modo === 'venda') {
      return [
        {
          label: 'Domínio',
          valor: k.devolucao_venda,
          cor: FONTE_COR.dom,
          serie: serie(s => s.devolucao_venda),
        },
        {
          label: 'Autmais',
          valor: k.devolucao_fiscal_venda,
          cor: FONTE_COR.aut,
          serie: serie(s => s.devolucao_fiscal_venda),
        },
      ];
    }
    return [
      {
        label: 'Domínio',
        valor: k.devolucao_notas,
        cor: FONTE_COR.dom,
        serie: serie(s => s.devolucao),
      },
      {
        label: 'Autmais',
        valor: k.devolucao_fiscal_propria,
        cor: FONTE_COR.aut,
        serie: serie(s => s.devolucao_fiscal_propria),
      },
    ];
  };
  const info =
    modo === 'venda'
      ? 'Devolução de VENDA (entrada): o cliente devolve para a empresa. Domínio pela natureza (Devolu%) nas entradas; Autmais pela finalidade=4 nas notas de entrada. Divergência = devolução capturada mas não lançada.'
      : 'Devolução PRÓPRIA (saída): a empresa devolve ao fornecedor (emite a nota). Domínio pela natureza nas saídas; Autmais pela finalidade=4 nas notas de saída.';
  return (
    <KpiPorFonte
      titulo="Devolução"
      accent="#8B5CF6"
      icon={<ArrowUDownLeft size={17} weight="duotone" />}
      positiveWhenDown
      info={info}
      fontes={montaFontes()}
      titleToggle={
        <MiniToggle
          value={modo}
          onChange={setModo}
          options={[
            ['venda', 'Venda'],
            ['propria', 'Própria'],
          ]}
        />
      }
    />
  );
};

// -------------------------------------------------------------------
// Sub-card de fonte dentro do card da empresa
// -------------------------------------------------------------------
const TIPOS: Array<{ k: keyof IContagemTipos; lab: string }> = [
  { k: 'nfe', lab: 'NFE' },
  { k: 'nfce', lab: 'NFCE' },
  { k: 'cte', lab: 'CTE' },
  { k: 'nfse', lab: 'NFSE' },
];

interface FonteProps {
  titulo: string;
  cls: 'dom' | 'fis' | 'por';
  fonte: IFonteContagem;
  referencia?: IFonteContagem;
  autmaisStatus?: AutmaisStatus | null;
  onAutmaisClick?: () => void;
  /** Veredito nota-a-nota da célula (mesma fonte da coluna ✓/✕). Sem isso a cor
   *  cairia na contagem, e "12 = 12" com notas diferentes ficaria verde
   *  contradizendo o ✕ ao lado. */
  veredito?: (dir: 'entrada' | 'saida', tk: keyof IContagemTipos) => ConfSt;
}
const CORES = {
  dom: { bg: 'rgba(27,136,245,0.055)', bd: 'rgba(27,136,245,0.16)', fg: '#1B88F5' },
  fis: { bg: 'rgba(245,158,11,0.06)', bd: 'rgba(245,158,11,0.18)', fg: '#b45309' },
  por: { bg: 'rgba(139,92,246,0.06)', bd: 'rgba(139,92,246,0.16)', fg: '#6d28d9' },
};
// Selo de status da captura Autmais (canto direito do subcard Autmais).
const AUT_SELO: Record<AutmaisStatus, { bg: string; fg: string; lab: string; short: string }> = {
  ok: { bg: 'rgba(16,185,129,0.14)', fg: '#047857', lab: 'Captura OK', short: 'OK' },
  aviso: { bg: 'rgba(245,158,11,0.18)', fg: '#b45309', lab: 'Captura c/ aviso', short: 'Aviso' },
  erro: { bg: 'rgba(239,68,68,0.14)', fg: '#b91c1c', lab: 'Captura com erro', short: 'Erro' },
  processando: { bg: 'rgba(59,130,246,0.14)', fg: '#1d4ed8', lab: 'Processando', short: 'Proc.' },
  sem_log: { bg: 'rgba(148,163,184,0.16)', fg: '#64748B', lab: 'Sem log', short: 'Sem log' },
};
const FonteCard = ({
  titulo,
  cls,
  fonte,
  referencia,
  autmaisStatus,
  onAutmaisClick,
  veredito,
}: FonteProps) => {
  const c = CORES[cls];
  const selo = cls === 'fis' && autmaisStatus ? AUT_SELO[autmaisStatus] : null;
  const linha = (
    lab: string,
    v: number | null,
    r: number | null,
    flag: { dev: number; canc: number },
    dir: 'entrada' | 'saida',
    fatura?: number,
    tk?: keyof IContagemTipos,
  ) => {
    const na = v === null; // a fonte nao cobre esse tipo de nota → mostra "—"
    let color = na ? '#CBD5E1' : '#475569';
    if (!na && r != null) {
      // Vermelho: a fonte tem MAIS que o Domínio (nota emitida e não lançada).
      // Amarelo: tem MENOS (o Domínio lançou algo que a fonte não capturou).
      // Verde: bate — mas só se o nota-a-nota confirmar. "12 = 12" com notas
      // diferentes continua neutro, senão a cor mentiria contra o ✕ ao lado.
      if (v > r) color = '#EF4444';
      else if (v < r) color = '#F59E0B';
      else if (v > 0 && tk && veredito?.(dir, tk) === 'ok') color = '#047857';
    }
    const devChar = dir === 'entrada' ? '↩' : '⟲';
    const devTtl =
      dir === 'entrada' ? 'Devolução de venda (entrada)' : 'Devolução própria — a empresa emite';
    return (
      <Flex
        key={lab}
        justify="space-between"
        align="center"
        fontSize="11.5px"
        lineHeight="1.75"
        gap={1}
      >
        <Text color="#94A3B8" fontWeight={600}>
          {lab}:
        </Text>
        <Flex align="center" gap={1}>
          {/* Badges de dev/cancelada vem PRIMEIRO (logo apos o label) e o
              numero principal (contagem ATIVA da fonte) por ULTIMO, alinhado
              a' direita numa largura fixa — forma uma coluna reta de numeros
              entre as linhas, que a ordem anterior (numero antes do badge)
              quebrava, pois a largura do badge variava linha a linha. Sem
              badge nenhum, mostra "–" no lugar deles (mesmo padrao de "sem
              dado" usado no resto do card). */}
          {!(flag.dev > 0 || flag.canc > 0) && <Text color="#CBD5E1">–</Text>}
          <FonteTag
            n={flag.dev}
            char={devChar}
            cor="#6d28d9"
            bg="rgba(139,92,246,0.12)"
            titulo={devTtl}
          />
          <FonteTag
            n={flag.canc}
            char="✕"
            cor="#b91c1c"
            bg="rgba(239,68,68,0.10)"
            titulo={
              `${flag.canc} cancelada(s) — NÃO entram na contagem ao lado. ` +
              `A contagem mostra só as ativas; no detalhe nota-a-nota as ` +
              `canceladas aparecem na lista com a tag ✕, então "ativas + ✕" ` +
              `é o total que o detalhe exibe.`
            }
          />
          <Text
            fontWeight={700}
            color={color}
            minW="24px"
            textAlign="right"
            sx={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {na ? '—' : v}
          </Text>
          {fatura != null && fatura > 0 && (
            <Tooltip
              label={`Fatura (codi_esp 42): ${fatura}. Documento de serviço do Domínio — fora da comparação.`}
              fontSize="11px"
              hasArrow
              placement="top"
              bg="#0F172A"
              color="#F1F5F9"
              borderRadius="lg"
              px={2.5}
              py={2}
              maxW="220px"
            >
              <Flex align="center" gap="3px" cursor="help" onClick={e => e.stopPropagation()}>
                <Text color="#CBD5E1">|</Text>
                <Text color="#6d28d9" fontWeight={600} opacity={0.75}>
                  FAT.
                </Text>
                <Text color="#6d28d9" fontWeight={700} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                  {fatura}
                </Text>
              </Flex>
            </Tooltip>
          )}
        </Flex>
      </Flex>
    );
  };
  const bloco = (titulo2: string, dir: 'entrada' | 'saida') => {
    const flags = dir === 'entrada' ? fonte.entrada_flags : fonte.saida_flags;
    return (
      <>
        <Text
          fontSize="10px"
          letterSpacing="0.05em"
          textTransform="uppercase"
          fontWeight={700}
          color="#94A3B8"
          mt={2}
          mb={1}
        >
          {titulo2}
        </Text>
        {TIPOS.map(t => {
          let fat: number | undefined;
          if (t.k === 'nfse' && cls === 'dom') {
            fat = dir === 'entrada' ? fonte.fatura_entrada : fonte.fatura_saida;
          }
          return linha(
            t.lab,
            fonte[dir][t.k],
            referencia ? referencia[dir][t.k] : null,
            flags[t.k],
            dir,
            fat,
            t.k,
          );
        })}
      </>
    );
  };
  return (
    <Box borderRadius="13px" p="11px 12px" border="1px solid" borderColor={c.bd} bg={c.bg}>
      <Flex align="center" justify="space-between" gap={1} mb={2} minH="19px">
        <Text fontSize="12px" fontWeight={750} color={c.fg}>
          {titulo}
        </Text>
        {selo && (
          <Badge
            as="button"
            type="button"
            onClick={e => {
              e.stopPropagation();
              onAutmaisClick?.();
            }}
            display="flex"
            alignItems="center"
            gap={1}
            bg={selo.bg}
            color={selo.fg}
            borderRadius="20px"
            px={2}
            py="2px"
            fontSize="9px"
            fontWeight={700}
            textTransform="none"
            cursor="pointer"
            title={`${selo.lab} — ver log de captura`}
            _hover={{ filter: 'brightness(0.97)' }}
          >
            <Box as="span" w="5px" h="5px" borderRadius="50%" bg={selo.fg} flexShrink={0} />
            Captura Autmais
          </Badge>
        )}
      </Flex>
      {bloco('Entrada', 'entrada')}
      {bloco('Saída', 'saida')}
      <Text
        mt={2}
        pt={2}
        borderTop="1px solid rgba(15,23,42,0.08)"
        textAlign="center"
        fontSize="12.5px"
        fontWeight={750}
      >
        Total: {fonte.total}
      </Text>
    </Box>
  );
};

// Tag compacta (ícone + contagem) no canto esquerdo do Total de cada fonte.
const FonteTag = ({
  n,
  char,
  cor,
  bg,
  titulo,
}: {
  n: number;
  char: string;
  cor: string;
  bg: string;
  titulo: string;
}) => {
  if (!n) return null;
  return (
    <Box
      as="span"
      title={titulo}
      display="inline-flex"
      alignItems="center"
      gap="2px"
      px="5px"
      py="1px"
      borderRadius="6px"
      bg={bg}
      color={cor}
      fontSize="10.5px"
      fontWeight={700}
      cursor="help"
      sx={{ fontVariantNumeric: 'tabular-nums' }}
    >
      {char}
      {n}
    </Box>
  );
};

// Rótulo dos vereditos. "Erro" fazia pensar em falha do robô — e o robô pode ter
// rodado perfeito e a empresa ainda estar divergente. A palavra que o supervisor
// usa no dia a dia é *divergência*. A trilha e o badge do card falam o mesmo idioma.
const VEREDITO_LAB: Record<StatusConf, string> = {
  erro: 'Divergência',
  aviso: 'Atenção',
  ok: 'Conferido',
  sem_dados: 'Sem dados',
};
const VEREDITO_COR: Record<StatusConf, string> = {
  erro: '#b91c1c',
  aviso: '#b45309',
  ok: '#047857',
  sem_dados: '#64748B',
};
// Ordem por SEVERIDADE, fixa entre meses: memória muscular é metade do valor de
// uma fila de trabalho (o chip não pode trocar de posição conforme o volume).
const VEREDITO_ORDEM: StatusConf[] = ['erro', 'aviso', 'ok', 'sem_dados'];
// Captura do robô — eixo INDEPENDENTE do veredito. Uma empresa "Conferido" com
// captura "Sem log" não está conferida: é uma empresa sobre a qual não há o que
// afirmar, porque o robô não trouxe nada pra comparar.
const CAPTURA_ORDEM: AutmaisStatus[] = ['erro', 'processando', 'aviso', 'sem_log', 'ok'];
const CAPTURA_LAB: Record<AutmaisStatus, string> = {
  erro: 'Captura com erro',
  processando: 'Capturando',
  aviso: 'Captura c/ aviso',
  sem_log: 'Sem captura',
  ok: 'Capturado',
};
const CAPTURA_COR: Record<AutmaisStatus, string> = {
  erro: '#b91c1c',
  processando: '#1d4ed8',
  aviso: '#b45309',
  sem_log: '#7c3aed',
  ok: '#047857',
};
const CAPTURA_DICA: Record<AutmaisStatus, string> = {
  erro: 'O robô falhou ao baixar as notas — não há base de comparação confiável.',
  processando: 'Captura em andamento; os números ainda podem mudar.',
  aviso: 'Capturou com ressalva (ex.: empresa sem o CNAE para emitir aquele documento).',
  sem_log: 'Nenhum registro de captura no mês: a conferência desta empresa não tem o que comparar.',
  ok: 'Robô rodou sem erro no período.',
};

const VEREDITO_DICA: Record<StatusConf, string> = {
  erro: 'Outra fonte tem nota que falta no Domínio — provável lançamento faltando.',
  aviso: 'O Domínio tem nota que a outra fonte não capturou.',
  ok: 'Todas as fontes bateram, nota a nota.',
  sem_dados: 'Nenhuma fonte trouxe nota neste mês.',
};

const BADGE: Record<StatusConf, { bg: string; fg: string; lab: string; icon: React.ReactNode }> = {
  ok: {
    bg: 'rgba(16,185,129,0.12)',
    fg: VEREDITO_COR.ok,
    lab: VEREDITO_LAB.ok,
    icon: <CheckCircle weight="fill" size={13} />,
  },
  erro: {
    bg: 'rgba(239,68,68,0.12)',
    fg: VEREDITO_COR.erro,
    lab: VEREDITO_LAB.erro,
    icon: <WarningCircle weight="fill" size={13} />,
  },
  aviso: {
    bg: 'rgba(245,158,11,0.15)',
    fg: VEREDITO_COR.aviso,
    lab: VEREDITO_LAB.aviso,
    icon: <WarningCircle weight="fill" size={13} />,
  },
  sem_dados: {
    bg: 'rgba(148,163,184,0.14)',
    fg: VEREDITO_COR.sem_dados,
    lab: VEREDITO_LAB.sem_dados,
    icon: <MinusCircle weight="fill" size={13} />,
  },
};

// Tinta bem sutil do card inteiro pela cor do status, pra bater o olho no grid.
// O GlassCard já aplica a 8%; o alpha do rgba multiplica, deixando ~3-4% efetivo.
const CARD_TINT: Record<StatusConf, string> = {
  ok: 'rgba(16,185,129,0.45)',
  erro: 'rgba(239,68,68,0.5)',
  aviso: 'rgba(245,158,11,0.45)',
  sem_dados: 'rgba(148,163,184,0.35)',
};

// -------------------------------------------------------------------
// Coluna de conferência (4º subcard fino): ✓ / ✕ por (sentido, tipo), comparando
// o Domínio (referência) com a fonte que cobre aquele tipo — Autmais (NF-e/NFC-e/
// CT-e) ou Portal (NFS-e). O tooltip explica a comparação e a regra de captura.
// -------------------------------------------------------------------
const REGRA_CAPTURA: Record<keyof IContagemTipos, string> = {
  nfe: 'NF-e: entrada é captura nacional; saída identificada por estado.',
  nfce: 'NFC-e: saída identificada por estado (Autmais).',
  cte: 'CT-e: captura nacional (entrada e saída).',
  nfse: 'NFS-e: Portal Nacional, identificada por município.',
};

type ConfSt = 'ok' | 'diff' | 'na';

// Veredito de uma célula: compara Domínio x fonte que cobre o tipo.
const confDe = (
  c: IEmpresaCard,
  dir: 'entrada' | 'saida',
  tk: keyof IContagemTipos,
): {
  st: ConfSt;
  d: number;
  p: number;
  par: string;
  soDom: number;
  soOut: number;
  sitDiff: number;
} => {
  const dom = c.dominio[dir][tk];
  const d = dom ?? 0;

  // veredito do nota-a-nota (autoritativo): ✕ se há nota só num lado (so_dom/so_out) OU
  // nota casada com SITUAÇÃO divergente (sit_diff — ex.: cancelada só numa fonte).
  const verd = (cel: IDiffCelula, p: number, par: string) => {
    const sd = cel.sit_diff ?? 0;
    const st: ConfSt = cel.so_dom === 0 && cel.so_out === 0 && sd === 0 ? 'ok' : 'diff';
    return { st, d, p, par, soDom: cel.so_dom, soOut: cel.so_out, sitDiff: sd };
  };

  // NFS-e tem 3 fontes: compara contra a que capturou MAIS notas (empate → Portal).
  if (tk === 'nfse') {
    const aut = c.fiscal[dir][tk] ?? 0;
    const por = c.portal[dir][tk] ?? 0;
    const par = aut > por ? 'Autmais' : 'Portal';
    const p = Math.max(aut, por);
    const cel = c.conferencia?.[dir]?.[tk];
    if (d === 0 && p === 0 && !cel)
      return { st: 'na' as ConfSt, d, p, par, soDom: 0, soOut: 0, sitDiff: 0 };
    if (cel) return verd(cel, p, par);
    return {
      st: (d === p ? 'ok' : 'diff') as ConfSt,
      d,
      p,
      par,
      soDom: Math.max(0, d - p),
      soOut: Math.max(0, p - d),
      sitDiff: 0,
    };
  }

  // Mercadoria: Domínio × Autmais (nota-a-nota autoritativo).
  const parVal = c.fiscal[dir][tk];
  const par = 'Autmais';
  const p = parVal ?? 0;
  const cel = c.conferencia?.[dir]?.[tk];
  if ((dom == null && parVal == null) || (d === 0 && p === 0 && !cel)) {
    return { st: 'na' as ConfSt, d, p, par, soDom: 0, soOut: 0, sitDiff: 0 };
  }
  if (cel) return verd(cel, p, par);
  // fallback por contagem quando o diff nota-a-nota nao veio.
  return { st: (d === p ? 'ok' : 'diff') as ConfSt, d, p, par, soDom: 0, soOut: 0, sitDiff: 0 };
};

const CONF_CHAR: Record<ConfSt, { char: string; cor: string }> = {
  ok: { char: '✓', cor: '#047857' },
  diff: { char: '✕', cor: '#b91c1c' },
  na: { char: '–', cor: '#CBD5E1' },
};

const ConfLinha = ({
  c,
  dir,
  tk,
  lab,
}: {
  c: IEmpresaCard;
  dir: 'entrada' | 'saida';
  tk: keyof IContagemTipos;
  lab: string;
}) => {
  const { st, d, par, soDom, soOut, sitDiff } = confDe(c, dir, tk);
  const info = CONF_CHAR[st];
  const sentido = dir === 'entrada' ? 'entrada' : 'saída';
  let tip: string;
  if (st === 'na') {
    tip = `${lab} ${sentido}: sem notas para comparar.`;
  } else if (st === 'ok') {
    tip = `${lab} ${sentido} — as notas do Domínio batem nota a nota com o ${par}. ${REGRA_CAPTURA[tk]}`;
  } else {
    const partes: string[] = [];
    if (soDom > 0) partes.push(`${soDom} só no Domínio`);
    if (soOut > 0) partes.push(`${soOut} só no ${par}`);
    if (sitDiff > 0) partes.push(`${sitDiff} com situação divergente (ex.: cancelada só num lado)`);
    const detalhe = partes.length ? partes.join(' · ') : `${d} × ${par}`;
    tip = `${lab} ${sentido} — divergência nota a nota: ${detalhe}. ${REGRA_CAPTURA[tk]}`;
  }
  return (
    <Tooltip
      label={tip}
      fontSize="11.5px"
      hasArrow
      placement="left"
      bg="#0F172A"
      color="#F1F5F9"
      borderRadius="lg"
      px={3}
      py={2}
      maxW="260px"
      lineHeight="1.4"
      openDelay={120}
    >
      <Flex
        justify="center"
        align="center"
        fontSize="12px"
        lineHeight="1.75"
        fontWeight={800}
        color={info.cor}
        cursor="help"
        onClick={e => e.stopPropagation()}
      >
        {info.char}
      </Flex>
    </Tooltip>
  );
};

const ConfCard = ({ c }: { c: IEmpresaCard }) => {
  const vereditos = (['entrada', 'saida'] as const).flatMap(dir =>
    TIPOS.map(t => confDe(c, dir, t.k).st),
  );
  let geral: ConfSt = 'na';
  if (vereditos.includes('diff')) geral = 'diff';
  else if (vereditos.includes('ok')) geral = 'ok';
  const gInfo = CONF_CHAR[geral];

  const bloco = (dir: 'entrada' | 'saida') => (
    <>
      {/* header invisível só p/ alinhar com o ENTRADA/SAÍDA dos subcards */}
      <Text fontSize="10px" mt={2} mb={1} color="transparent" aria-hidden>
        ·
      </Text>
      {TIPOS.map(t => (
        <ConfLinha key={t.k} c={c} dir={dir} tk={t.k} lab={t.lab} />
      ))}
    </>
  );

  return (
    <Box
      borderRadius="13px"
      p="11px 4px"
      border="1px solid"
      borderColor="rgba(15,23,42,0.08)"
      bg="rgba(15,23,42,0.015)"
    >
      <Flex align="center" justify="center" mb={2} minH="19px">
        <Tooltip
          label="Conferência: ✓ as fontes batem, ✕ divergem, – nada a comparar. Passe o mouse em cada linha."
          fontSize="11px"
          hasArrow
          placement="top"
          bg="#0F172A"
          color="#F1F5F9"
          borderRadius="lg"
          px={2.5}
          py={2}
          maxW="230px"
        >
          <Text fontSize="11px" fontWeight={750} color="#64748B" cursor="help">
            ✓/✕
          </Text>
        </Tooltip>
      </Flex>
      {bloco('entrada')}
      {bloco('saida')}
      <Flex
        mt={2}
        pt={2}
        borderTop="1px solid rgba(15,23,42,0.08)"
        justify="center"
        fontSize="13px"
        fontWeight={800}
        color={gInfo.cor}
      >
        {gInfo.char}
      </Flex>
    </Box>
  );
};

const EmpresaCard = ({
  c,
  onClick,
  onAutmais,
  onDuplicadas,
  duplicadas,
  sentidoFiltro,
}: {
  c: IEmpresaCard;
  onClick: () => void;
  onAutmais: () => void;
  onDuplicadas?: () => void;
  duplicadas?: IDuplicadasResumo;
  sentidoFiltro?: string | null;
}) => {
  const b = BADGE[c.status];
  // mesma fonte de verdade da coluna ✓/✕, pra cor da célula nunca contradizê-la
  const vered = (dir: 'entrada' | 'saida', tk: keyof IContagemTipos) => confDe(c, dir, tk).st;
  return (
    <GlassCard
      intensity="medium"
      hoverable
      tint={CARD_TINT[c.status]}
      p="16px 17px"
      cursor="pointer"
      onClick={onClick}
    >
      <Flex direction="column" align="center" gap="3px" position="relative" mb={2}>
        {duplicadas && duplicadas.quantidade_duplicadas > 0 && (
          <Tooltip
            label={`${duplicadas.quantidade_duplicadas} nota(s) duplicada(s) no Domínio (${duplicadas.quantidade_entrada} entrada, ${duplicadas.quantidade_saida} saída). Clique para ver.`}
            fontSize="11px"
            hasArrow
            placement="top"
            bg="#0F172A"
            color="#F1F5F9"
            borderRadius="lg"
            px={3}
            py={2}
            maxW="280px"
          >
            <Badge
              position="absolute"
              left={0}
              top={0}
              bg="rgba(245,158,11,0.15)"
              color="#b45309"
              borderRadius="8px"
              px={2}
              py={1}
              display="flex"
              alignItems="center"
              gap="3px"
              fontSize="10px"
              fontWeight={700}
              textTransform="none"
              cursor="pointer"
              _hover={{ bg: 'rgba(245,158,11,0.28)' }}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                onDuplicadas?.();
              }}
            >
              <Copy size={11} weight="bold" />
              {duplicadas.quantidade_duplicadas} Notas duplicadas
            </Badge>
          </Tooltip>
        )}
        <Badge
          position="absolute"
          right={0}
          top={0}
          bg={b.bg}
          color={b.fg}
          borderRadius="20px"
          px={2.5}
          py={1}
          display="flex"
          alignItems="center"
          gap={1}
          fontSize="11px"
          textTransform="none"
        >
          {b.icon} {sentidoFiltro ? `Geral: ${b.lab}` : b.lab}
        </Badge>
        <Text fontSize="11px" fontFamily="mono" color="#94A3B8">
          ID: {c.external_id}
        </Text>
        <Text fontWeight={700} fontSize="14.5px" letterSpacing="-0.01em">
          {c.razao_social || '—'}
        </Text>
        <Text fontSize="11.5px" fontFamily="mono" color="#94A3B8">
          {c.cnpj || '—'}
        </Text>
      </Flex>
      <Grid templateColumns={{ base: '1fr', sm: '1fr 1fr 1fr 34px' }} gap="10px">
        <FonteCard titulo="Domínio" cls="dom" fonte={c.dominio} />
        <FonteCard
          titulo="Autmais"
          cls="fis"
          fonte={c.fiscal}
          referencia={c.dominio}
          veredito={vered}
          autmaisStatus={c.autmais_status ?? 'sem_log'}
          onAutmaisClick={onAutmais}
        />
        <FonteCard
          titulo="Portal Nac."
          cls="por"
          fonte={c.portal}
          referencia={c.dominio}
          veredito={vered}
        />
        <ConfCard c={c} />
      </Grid>
      <Flex
        gap={2}
        mt={3}
        justify="center"
        align="center"
        fontSize="11.5px"
        color="#475569"
        fontWeight={600}
        flexWrap="wrap"
      >
        <Text color="#94A3B8">UF:</Text> {c.uf || '—'} · <Text color="#94A3B8">Município:</Text>{' '}
        {c.municipio || '—'}
        {c.situacao_divergente > 0 && (
          <>
            {' · '}
            <Tooltip
              label={`${c.situacao_divergente} nota(s) existem nas duas fontes, mas com situação diferente (cancelada só de um lado). As contagens batem — as notas, não.`}
              fontSize="11px"
              hasArrow
              placement="top"
              bg="#0F172A"
              color="#F1F5F9"
              borderRadius="lg"
              px={3}
              py={2}
              maxW="260px"
            >
              <Badge
                bg="rgba(239,68,68,0.12)"
                color="#b91c1c"
                borderRadius="6px"
                px={1.5}
                fontSize="10px"
                fontWeight={700}
                textTransform="none"
                cursor="help"
              >
                {c.situacao_divergente} situação divergente
              </Badge>
            </Tooltip>
          </>
        )}
      </Flex>
      {/* Valor por fonte: prioriza a empresa pelo tamanho do buraco, não pela
          contagem — uma NF-e de R$ 480k não pode competir com um cupom de R$ 12. */}
      <Flex
        gap={3}
        mt={1.5}
        justify="center"
        align="center"
        fontSize="10.5px"
        fontWeight={600}
        flexWrap="wrap"
        sx={{ fontVariantNumeric: 'tabular-nums' }}
      >
        <Text color="#94A3B8">R$</Text>
        <Text color={FONTE_COR.dom}>{fmtValor(c.valor_dominio)}</Text>
        <Text color={FONTE_COR.aut}>{fmtValor(c.valor_fiscal)}</Text>
        <Text color={FONTE_COR.por}>{fmtValor(c.valor_portal)}</Text>
        {c.divergencia_notas > 0 && (
          <Tooltip
            label={`${c.divergencia_notas} nota(s) não fecham e R$ ${fmtValor(
              c.valor_divergente,
            )} separam Domínio e Autmais. É por aqui que a fila de trabalho é priorizada.`}
            fontSize="11px"
            hasArrow
            placement="top"
            bg="#0F172A"
            color="#F1F5F9"
            borderRadius="lg"
            px={3}
            py={2}
            maxW="250px"
          >
            <Badge
              bg="rgba(239,68,68,0.1)"
              color="#b91c1c"
              borderRadius="6px"
              px={1.5}
              fontSize="10px"
              fontWeight={700}
              textTransform="none"
              cursor="help"
            >
              Δ {c.divergencia_notas} · {fmtValor(c.valor_divergente)}
            </Badge>
          </Tooltip>
        )}
        <InfoTip label="Valor das notas COMPARÁVEIS por fonte: Domínio · Autmais · Portal. Só documento fiscal eletrônico — rendimento de aplicação e outras espécies que não vão à SEFAZ ficam de fora (senão inflariam a divergência). O Portal só tem NFS-e, então é naturalmente menor." />
      </Flex>
    </GlassCard>
  );
};

// -------------------------------------------------------------------
// Página
// -------------------------------------------------------------------
export const ConferenciaFiscalPage = () => {
  // Sem mês na URL, abre no mês ANTERIOR, que é o mês em fechamento. O
  // backend mantém esse mês (e o corrente) sempre calculado no cache.
  const hoje = new Date();
  const mesPadrao = new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1);
  const [params, setParams] = useSearchParams();
  // URL como fonte de verdade dos filtros: permite mandar o link pronto ("resolve
  // essas 12 ate' sexta"), sobrevive a F5 e a voltar de outra tela. Param invalido
  // cai no default em silencio — o link vai ser editado a mao e colado torto.
  const pAno = Number(params.get('ano'));
  const pMes = Number(params.get('mes'));
  const pVer = params.get('v');
  const [ano, setAno] = useState(pAno >= 2000 && pAno <= 2100 ? pAno : mesPadrao.getFullYear());
  const [mes, setMes] = useState(pMes >= 1 && pMes <= 12 ? pMes - 1 : mesPadrao.getMonth());
  const [page, setPage] = useState(Math.max(1, Number(params.get('p')) || 1));
  const [vereditoFiltro, setVereditoFiltro] = useState<StatusConf | null>(
    // valida por LISTA, não por lookup: `VEREDITO_LAB['toString']` é truthy
    // (herdado de Object.prototype) e `?v=toString` deixaria a tela num filtro
    // inválido do qual não há como sair pela interface.
    VEREDITO_ORDEM.includes(pVer as StatusConf) ? (pVer as StatusConf) : null,
  );
  const pCap = params.get('c');
  const [capturaFiltro, setCapturaFiltro] = useState<AutmaisStatus | null>(
    CAPTURA_ORDEM.includes(pCap as AutmaisStatus) ? (pCap as AutmaisStatus) : null,
  );
  const pOrd = params.get('o');
  const [ordem, setOrdem] = useState<OrdemEmpresas>(
    (['valor_divergente', 'divergencia'] as const).includes(pOrd as never)
      ? (pOrd as OrdemEmpresas)
      : 'padrao',
  );
  const [empresaSel, setEmpresaSel] = useState<IEmpresaCard | null>(null);
  const [logAutmais, setLogAutmais] = useState<IEmpresaCard | null>(null);
  const [dupEmpresa, setDupEmpresa] = useState<IEmpresaCard | null>(null);
  const [busca, setBusca] = useState(params.get('q') ?? '');
  const [buscaModo, setBuscaModo] = useState<EmpresaBuscaModo>(
    (['razao', 'cnpj', 'id'] as const).includes(params.get('qm') as EmpresaBuscaModo)
      ? (params.get('qm') as EmpresaBuscaModo)
      : 'id',
  );
  const [buscaDeb, setBuscaDeb] = useState(params.get('q') ?? '');
  // Filtros avançados (drawer). Lidos da URL na montagem; só aplicam no "Filtrar".
  const [avancados, setAvancados] = useState<IFiltrosAvancados>(() => lerAvancados(params));
  const [drawerFiltros, setDrawerFiltros] = useState(false);
  const SS_VALID = ['entrada_erro', 'entrada_ok', 'saida_erro', 'saida_ok'] as const;
  type SentidoStatus = (typeof SS_VALID)[number];
  const pSS = params.get('ss');
  const [sentidoFiltro, setSentidoFiltro] = useState<SentidoStatus | null>(
    SS_VALID.includes(pSS as SentidoStatus) ? (pSS as SentidoStatus) : null,
  );
  const DUP_VALID = ['todas', 'entrada', 'saida'] as const;
  type DupFiltro = (typeof DUP_VALID)[number];
  const pDup = params.get('dup');
  const [dupFiltro, setDupFiltro] = useState<DupFiltro | null>(
    DUP_VALID.includes(pDup as DupFiltro) ? (pDup as DupFiltro) : null,
  );

  // debounce da busca (server-side) + volta pra primeira página ao filtrar
  useEffect(() => {
    const t = setTimeout(() => setBuscaDeb(busca.trim()), 350);
    return () => clearTimeout(t);
  }, [busca]);
  // Volta pra página 1 quando o usuário MUDA um filtro — mas não na montagem,
  // senão um link com `?p=3` abriria na página 1 e o param seria apagado.
  const montado = useRef(false);
  useEffect(() => {
    if (!montado.current) {
      montado.current = true;
      return;
    }
    setPage(1);
  }, [
    buscaDeb,
    buscaModo,
    vereditoFiltro,
    capturaFiltro,
    sentidoFiltro,
    dupFiltro,
    ordem,
    avancados,
  ]);

  // Espelha o estado na URL. `replace` sempre: com debounce da busca, `push`
  // encheria o historico e o botao Voltar do navegador ficaria inutilizavel.
  useEffect(() => {
    const p = new URLSearchParams();
    p.set('ano', String(ano));
    p.set('mes', String(mes + 1));
    if (vereditoFiltro) p.set('v', vereditoFiltro);
    if (capturaFiltro) p.set('c', capturaFiltro);
    if (ordem !== 'padrao') p.set('o', ordem);
    if (buscaDeb) p.set('q', buscaDeb);
    // `qm` independe de `q`: quem escolhe "CNPJ", apaga a busca e dá F5 voltava
    // silenciosamente pro modo padrão (Id) e digitava o CNPJ no modo errado.
    if (buscaModo !== 'id') p.set('qm', buscaModo);
    if (sentidoFiltro) p.set('ss', sentidoFiltro);
    if (dupFiltro) p.set('dup', dupFiltro);
    escreveAvancados(p, avancados);
    if (page > 1) p.set('p', String(page));
    setParams(p, { replace: true });
  }, [
    ano,
    mes,
    vereditoFiltro,
    capturaFiltro,
    ordem,
    buscaDeb,
    buscaModo,
    sentidoFiltro,
    dupFiltro,
    avancados,
    page,
    setParams,
  ]);

  const range = useMemo(() => rangeDoMes(ano, mes), [ano, mes]);
  const nAvancados = contaAvancados(avancados);

  const totais = useConferenciaTotais(range, false);
  const duplicadasQuery = useConferenciaDuplicadas(range);
  const duplicadasMap = useMemo(() => {
    const map = new Map<number, IDuplicadasResumo>();
    (duplicadasQuery.data ?? []).forEach(d => map.set(d.codi_emp, d));
    return map;
  }, [duplicadasQuery.data]);
  // Os MESMOS filtros alimentam a listagem e a exportação: o Excel sai com
  // exatamente o recorte que está na tela.
  const filtrosDaLista = {
    ...range,
    q: buscaDeb || undefined,
    q_modo: buscaModo,
    veredito: vereditoFiltro ?? undefined,
    captura: capturaFiltro ?? undefined,
    sentido_status: sentidoFiltro ?? undefined,
    duplicadas: dupFiltro ?? undefined,
    ordem,
    ...avancadosParaQuery(avancados),
  };
  const empresas = useConferenciaEmpresas({ ...filtrosDaLista, page, size: 30 });

  const toast = useToast();
  const [exportando, setExportando] = useState(false);
  // A planilha é gerada em SEGUNDO PLANO: o mês cheio leva minutos e o
  // balanceador de produção corta o pedido em 60 s. O botão só dispara; o aviso
  // de pronta chega no sino, com o botão "Baixar" (ver `exportacao.ts`).
  const exportarExcel = async () => {
    setExportando(true);
    try {
      const { ja_em_andamento: jaEmAndamento } = await iniciarExportacaoConferencia(filtrosDaLista);
      toast({
        title: jaEmAndamento ? 'A planilha já está sendo gerada' : 'Gerando a planilha',
        description: jaEmAndamento
          ? 'Você pediu uma exportação há pouco e ela ainda não terminou. O aviso chega no sino quando ficar pronta.'
          : `A planilha de ${MESES[mes]}/${ano} está sendo gerada em segundo plano. Pode continuar usando o sistema: o aviso chega no sino quando ficar pronta.`,
        status: 'info',
        duration: 8000,
        isClosable: true,
      });
    } catch {
      toast({
        title: 'Não foi possível iniciar a planilha',
        description: 'Tente de novo em alguns minutos.',
        status: 'error',
        duration: 8000,
        isClosable: true,
      });
    } finally {
      setExportando(false);
    }
  };

  // Clique na notificação "planilha pronta" abre a tela com `?baixar=<id>`.
  // O ref evita baixar duas vezes o mesmo arquivo (o servidor só entrega uma).
  const baixarPedido = params.get(PARAM_BAIXAR);
  const baixados = useRef(new Set<string>());
  useEffect(() => {
    if (!baixarPedido || baixados.current.has(baixarPedido)) return;
    baixados.current.add(baixarPedido);
    baixarComAviso(baixarPedido);
    setParams(
      prev => {
        const p = new URLSearchParams(prev);
        p.delete(PARAM_BAIXAR);
        return p;
      },
      { replace: true },
    );
  }, [baixarPedido, setParams]);

  const serieMensal = useConferenciaSerie(range, totais.isSuccess && empresas.isSuccess);

  // Sem o nota-a-nota o veredito é menos preciso e a trilha some — então um
  // filtro que tenha ficado ativo (ex.: vindo da URL) precisa cair junto, senão
  // a lista fica recortada por um critério que a tela não mostra mais.
  // (depende de `empresas`, por isso vem depois da query)
  const confOkResp = empresas.data?.conferencia_ok;
  useEffect(() => {
    if (confOkResp === false && vereditoFiltro) setVereditoFiltro(null);
  }, [confOkResp, vereditoFiltro]);

  // Clicar no chip ativo volta pra "Todas" (toggle, selecao unica).
  const clicaTriagem = (k: string) => {
    if (k === 'todas') {
      setCapturaFiltro(null);
      setSentidoFiltro(null);
      setDupFiltro(null);
    }
    setVereditoFiltro(prev => (k === 'todas' || prev === k ? null : (k as StatusConf)));
  };
  const clicaCaptura = (k: string) => {
    setCapturaFiltro(prev => (prev === k ? null : (k as AutmaisStatus)));
  };

  // Contadores vem do backend: respeitam periodo + busca, mas NUNCA o veredito
  // selecionado — senao clicar em "Divergencia" zeraria os demais e o supervisor
  // concluiria que o mes acabou.
  const cont = empresas.panorama?.contadores;
  // `!== false`: enquanto carrega (undefined) mantém a trilha visível, senão ela
  // pisca a cada request. Só some quando o backend afirma que falhou.
  const conferenciaOk = empresas.data?.conferencia_ok !== false;
  const chipsTriagem: ITriagemChip[] = useMemo(() => {
    // `?? 0` por chave (não só no objeto todo): se o payload vier com uma chave
    // a menos, `valor` seria undefined e o `.toLocaleString()` do chip derrubaria
    // a página inteira — não há error boundary nesse caminho.
    const valores = VEREDITO_ORDEM.map(v => Number(cont?.[v]) || 0);
    const total = valores.reduce((a, b) => a + b, 0);
    return [
      {
        k: 'todas',
        lab: 'Todas',
        cor: '#334155',
        valor: total,
        vazado: true,
        dica: 'Todas as empresas ativas no período (limpa o filtro).',
      },
      ...VEREDITO_ORDEM.map((v, i) => ({
        k: v,
        lab: VEREDITO_LAB[v],
        cor: VEREDITO_COR[v],
        valor: valores[i],
        vazado: v === 'sem_dados',
        dica: VEREDITO_DICA[v],
      })),
    ];
  }, [cont]);

  // Trilha da CAPTURA: só aparece quando há o que acionar (erro/sem log/fila).
  // Se o robô rodou em todo mundo, ela seria uma linha de zeros ocupando espaço.
  const contCap = empresas.panorama?.contadores_captura;
  const chipsCaptura: ITriagemChip[] = useMemo(
    () =>
      CAPTURA_ORDEM.map(k => ({
        k,
        lab: CAPTURA_LAB[k],
        cor: CAPTURA_COR[k],
        valor: Number(contCap?.[k]) || 0,
        vazado: k === 'sem_log',
        dica: CAPTURA_DICA[k],
      })).filter(c => c.valor > 0 || capturaFiltro === c.k),
    [contCap, capturaFiltro],
  );
  const mostraCaptura =
    chipsCaptura.some(c => c.k !== 'ok' && c.valor > 0) || capturaFiltro !== null;

  const mudaMes = (delta: number) => {
    let m = mes + delta;
    let a = ano;
    if (m < 0) {
      m = 11;
      a -= 1;
    }
    if (m > 11) {
      m = 0;
      a += 1;
    }
    setMes(m);
    setAno(a);
    setPage(1);
  };

  const k = totais.data;
  const serie = (sel: (s: ISerieMes) => number): number[] => (serieMensal.data ?? []).map(sel);

  const renderPaginacao = (posicao: 'superior' | 'inferior') => {
    if (!empresas.data?.pages || empresas.isError) return null;
    return (
      <Flex
        as="nav"
        aria-label={`Paginação ${posicao} das empresas`}
        justify="center"
        align="center"
        gap={3}
        mt={posicao === 'superior' ? 4 : 6}
        fontSize="13px"
        color="#475569"
      >
        <Button
          size="sm"
          variant="ghost"
          leftIcon={<CaretLeft />}
          isDisabled={page <= 1}
          onClick={() => setPage(p => Math.max(1, p - 1))}
        >
          Anterior
        </Button>
        <Text fontWeight={600}>
          Página {empresas.data.page} de {empresas.data.pages}
        </Text>
        <Button
          size="sm"
          variant="ghost"
          rightIcon={<CaretRight />}
          isDisabled={page >= empresas.data.pages}
          onClick={() => setPage(p => Math.min(empresas.data?.pages ?? p, p + 1))}
        >
          Próxima
        </Button>
      </Flex>
    );
  };

  const mensagemCarregamento = () => {
    if (empresas.isLoading) {
      const filtrado =
        vereditoFiltro || capturaFiltro || sentidoFiltro || dupFiltro || buscaDeb || nAvancados;
      return filtrado
        ? 'Aplicando filtros à lista de empresas…'
        : 'Carregando empresas do período…';
    }
    if (empresas.isFetching) {
      return empresas.isPlaceholderData
        ? 'Carregando a página selecionada…'
        : 'Conferindo dados mais recentes desta página…';
    }
    return totais.isLoading
      ? 'Carregando os indicadores do período…'
      : 'Atualizando os indicadores do período…';
  };

  const resumoSentido = () => {
    if (!empresas.data?.conferencia_ok) return 'com comparação indisponível.';
    return sentidoFiltro?.endsWith('_ok')
      ? 'sem divergência neste período.'
      : 'com divergência neste período.';
  };

  const renderEmpresas = () => {
    if (empresas.isLoading) {
      return (
        <Flex justify="center" py={16}>
          <Spinner color="#1B88F5" />
        </Flex>
      );
    }
    if (empresas.isError) {
      return (
        <GlassCard p={6}>
          <Text color="#b91c1c">Não foi possível carregar as empresas.</Text>
          {/* saída do filtro também aqui: se a query falhou por causa dele, os
              chips ficam sem contador e não haveria como limpá-lo pela tela. */}
          {vereditoFiltro && (
            <Button size="sm" variant="ghost" mt={3} onClick={() => setVereditoFiltro(null)}>
              Ver todas
            </Button>
          )}
        </GlassCard>
      );
    }
    if (!empresas.data?.items.length) {
      // Vazio FILTRADO != vazio real. Sem essa distinção o supervisor lê
      // "nenhuma empresa" e conclui que o mês está limpo, quando na verdade a
      // busca é que não cruzou com o veredito.
      const filtrado = vereditoFiltro || capturaFiltro || buscaDeb || nAvancados > 0;
      // Monta a frase por partes: interpolar 'busca' como se fosse o nome de uma
      // categoria produzia "Nenhuma empresa em "busca" corresponde a X".
      const recortes = [
        vereditoFiltro ? VEREDITO_LAB[vereditoFiltro].toLowerCase() : '',
        capturaFiltro ? CAPTURA_LAB[capturaFiltro].toLowerCase() : '',
      ].filter(Boolean);
      const alvo = recortes.length
        ? `Nenhuma empresa em ${recortes.join(' + ')}`
        : 'Nenhuma empresa';
      let vazioMsg: string;
      if (buscaDeb) vazioMsg = `${alvo} corresponde a "${buscaDeb}".`;
      else if (nAvancados > 0) vazioMsg = 'Nenhuma empresa neste recorte de filtros.';
      else vazioMsg = `${alvo} neste período.`;
      return (
        <GlassCard p={8}>
          <Text textAlign="center" color="#94A3B8">
            {filtrado ? vazioMsg : 'Nenhuma empresa com atividade neste período.'}
          </Text>
          {filtrado && (
            <Flex justify="center" gap={3} mt={4}>
              {buscaDeb && (
                <Button size="sm" variant="ghost" onClick={() => setBusca('')}>
                  Limpar busca
                </Button>
              )}
              {nAvancados > 0 && (
                <Button size="sm" variant="ghost" onClick={() => setAvancados({})}>
                  Limpar filtros avançados
                </Button>
              )}
              {(vereditoFiltro || capturaFiltro) && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setVereditoFiltro(null);
                    setCapturaFiltro(null);
                  }}
                >
                  Ver todas
                </Button>
              )}
            </Flex>
          )}
        </GlassCard>
      );
    }
    return (
      <>
        {/* Cicatriz do filtro: sem isso, "231 empresas" pode ser lido como o
            total e o mês ser fechado em cima de um subconjunto. */}
        {(vereditoFiltro || capturaFiltro) && (
          <Text fontSize="11px" color="#64748B" fontWeight={600} mb={2}>
            {empresas.data.total.toLocaleString('pt-BR')}{' '}
            {empresas.data.total === 1 ? 'empresa' : 'empresas'} em{' '}
            {vereditoFiltro && (
              <Box as="span" color={VEREDITO_COR[vereditoFiltro]}>
                {VEREDITO_LAB[vereditoFiltro].toLowerCase()}
              </Box>
            )}
            {vereditoFiltro && capturaFiltro && ' + '}
            {capturaFiltro && (
              <Box as="span" color={CAPTURA_COR[capturaFiltro]}>
                {CAPTURA_LAB[capturaFiltro].toLowerCase()}
              </Box>
            )}
            {ordem !== 'padrao' && (
              <Box as="span" color="#94A3B8" fontWeight={500}>
                {' '}
                · ordenado por{' '}
                {ordem === 'valor_divergente' ? 'R$ divergente' : 'nº de divergências'}
              </Box>
            )}
          </Text>
        )}
        {sentidoFiltro && (
          <Text fontSize="13px" color="#64748B" mb={3} role="status">
            {empresas.data.total.toLocaleString('pt-BR')} empresas ·{' '}
            {sentidoFiltro.startsWith('saida') ? 'Saídas' : 'Entradas'} {resumoSentido()} O status
            geral do cartão considera entradas e saídas.
          </Text>
        )}
        <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={4}>
          {empresas.data.items.map(c => (
            <EmpresaCard
              key={c.empresa_id}
              c={c}
              sentidoFiltro={sentidoFiltro}
              onClick={() => setEmpresaSel(c)}
              onAutmais={() => setLogAutmais(c)}
              onDuplicadas={() => setDupEmpresa(c)}
              duplicadas={duplicadasMap.get(c.external_id)}
            />
          ))}
        </SimpleGrid>
        {renderPaginacao('inferior')}
      </>
    );
  };

  return (
    <GlassPageBg>
      <Box maxW="1320px" mx="auto" px={6} py={7} pb={20}>
        {/* header */}
        <Flex align="center" gap={4} wrap="wrap" mb={6}>
          <Flex
            w="46px"
            h="46px"
            borderRadius="14px"
            align="center"
            justify="center"
            flex="none"
            bgGradient="linear(135deg, #1B88F5, #6366F1)"
            color="white"
            boxShadow="0 8px 20px -6px rgba(27,136,245,0.5)"
          >
            <Receipt size={24} weight="duotone" />
          </Flex>
          <Box>
            <Text
              fontSize="11px"
              letterSpacing="0.14em"
              textTransform="uppercase"
              color="#94A3B8"
              fontWeight={600}
            >
              Integração › Conferência de Importações
            </Text>
            <Text fontSize="22px" fontWeight={700} letterSpacing="-0.02em">
              Conferência de Importações
            </Text>
          </Box>
          <Box flex={1} />
          {(empresas.isFetching || totais.isFetching) && (
            <Flex role="status" aria-live="polite" align="center" gap={2}>
              <Spinner size="sm" color="#1B88F5" />
              <Text fontSize="13px" color="#64748B">
                {mensagemCarregamento()}
              </Text>
            </Flex>
          )}
          {!empresas.isFetching && horarioDoCalculo(empresas.data?.calculado_em) && (
            <Tooltip
              hasArrow
              label="A conferência é recalculada sozinha a cada 10 minutos, para a tela abrir na hora. Notas importadas depois desse horário entram no próximo cálculo."
            >
              <Text fontSize="12px" color="#64748B" cursor="help">
                Dados das {horarioDoCalculo(empresas.data?.calculado_em)}
              </Text>
            </Tooltip>
          )}
          {serieMensal.isFetching && (
            <Text role="status" fontSize="12px" color="#64748B">
              {serieMensal.isLoading
                ? 'Carregando histórico dos gráficos…'
                : 'Atualizando histórico dos gráficos…'}
            </Text>
          )}
          <Flex
            align="center"
            gap={2}
            bg="rgba(255,255,255,0.72)"
            backdropFilter="blur(20px)"
            border="1px solid rgba(15,23,42,0.08)"
            borderRadius="12px"
            px={2}
            py={1}
          >
            <IconButton
              aria-label="Mês anterior"
              icon={<CaretLeft />}
              size="sm"
              variant="ghost"
              onClick={() => mudaMes(-1)}
            />
            <Text fontSize="13.5px" fontWeight={600} minW="130px" textAlign="center">
              {MESES[mes]} de {ano}
            </Text>
            <IconButton
              aria-label="Próximo mês"
              icon={<CaretRight />}
              size="sm"
              variant="ghost"
              onClick={() => mudaMes(1)}
            />
          </Flex>
        </Flex>

        {/* KPIs */}
        <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={4}>
          <KpiEmpresas k={k} serie={serie} />
          <KpiPorFonte
            titulo="Notas por fonte"
            accent="#1B88F5"
            icon={<Receipt size={17} weight="duotone" />}
            info="Total de notas em cada fonte no período: Domínio (referência contábil), Autmais (XML capturado do share) e Portal (NFS-e nacional). A divergência aponta nota não importada ou não capturada."
            fontes={
              k
                ? [
                    {
                      label: 'Domínio',
                      valor: k.notas_dominio,
                      cor: FONTE_COR.dom,
                      serie: serie(s => s.notas_dominio),
                    },
                    {
                      label: 'Autmais',
                      valor: k.notas_fiscal,
                      cor: FONTE_COR.aut,
                      serie: serie(s => s.notas_fiscal),
                    },
                    {
                      label: 'Portal',
                      valor: k.notas_portal,
                      cor: FONTE_COR.por,
                      serie: serie(s => s.notas_portal),
                    },
                  ]
                : []
            }
          />
          <KpiDevolucao k={k} serie={serie} />
          <KpiPorFonte
            titulo="Canceladas por fonte"
            accent="#EF4444"
            icon={<XCircle size={17} weight="duotone" />}
            positiveWhenDown
            info="Notas canceladas em cada fonte (Domínio situacao=2, Autmais e Portal pela flag cancelada). A divergência entre as fontes é o sinal: ex. cancelada no Portal mas ainda baixada no Autmais, ou cancelada no Autmais e não baixada no Domínio."
            fontes={
              k
                ? [
                    {
                      label: 'Domínio',
                      valor: k.canceladas.dominio,
                      cor: FONTE_COR.dom,
                      serie: serie(s => s.canceladas_dominio),
                    },
                    {
                      label: 'Autmais',
                      valor: k.canceladas.fiscal,
                      cor: FONTE_COR.aut,
                      serie: serie(s => s.canceladas_fiscal),
                    },
                    {
                      label: 'Portal',
                      valor: k.canceladas.portal,
                      cor: FONTE_COR.por,
                      serie: serie(s => s.canceladas_portal),
                    },
                  ]
                : []
            }
          />
        </SimpleGrid>

        {/* grid de empresas */}
        {renderPaginacao('superior')}
        <Flex
          align={{ base: 'stretch', md: 'flex-end' }}
          justify="space-between"
          gap={3}
          mt={7}
          mb={3}
          direction={{ base: 'column', md: 'row' }}
          wrap="wrap"
        >
          <Flex direction="column" gap={2} minW={0}>
            {/* Trilha de triagem: particiona o total e define o escopo do grid.
              Substitui o rótulo "Empresas" — o chip "Todas" já diz o que é e
              quanto tem. Os 4 cards de cima continuam sendo o panorama do mês
              (nunca refletem este filtro). */}
            {conferenciaOk ? (
              <Flex
                gap={2}
                wrap="wrap"
                overflowX={{ base: 'auto', md: 'visible' }}
                pb={{ base: 1, md: 0 }}
              >
                {chipsTriagem.map(c => (
                  <TriagemChip
                    key={c.k}
                    c={c}
                    ativo={
                      c.k === 'todas'
                        ? !vereditoFiltro && !capturaFiltro && !sentidoFiltro && !dupFiltro
                        : vereditoFiltro === c.k
                    }
                    onClick={() => clicaTriagem(c.k)}
                  />
                ))}
              </Flex>
            ) : (
              /* Conferência nota-a-nota indisponível: o veredito caiu no fallback
               por contagem, então os contadores não são confiáveis. Some com a
               trilha e explica, em vez de exibir número que parece exato. */
              <Flex align="center" gap={2} wrap="wrap">
                <Badge
                  bg="rgba(245,158,11,0.15)"
                  color="#b45309"
                  borderRadius="8px"
                  px={2.5}
                  py={1}
                  fontSize="11px"
                  fontWeight={700}
                  textTransform="none"
                >
                  Conferência nota-a-nota indisponível
                </Badge>
                <Text fontSize="11.5px" color="#94A3B8">
                  os cards estão comparando por contagem; o filtro por status volta quando a
                  conferência detalhada rodar
                </Text>
              </Flex>
            )}

            {/* Segunda trilha: status do ROBÔ. Eixo independente — "Conferido" com
              "Sem captura" não é conferência, é ausência de base de comparação.
              Só aparece quando há algo a acionar (erro/fila/sem log). */}
            {mostraCaptura && (
              <Flex gap={2} wrap="wrap" align="center">
                <Text
                  fontSize="9.5px"
                  color="#94A3B8"
                  fontWeight={700}
                  textTransform="uppercase"
                  letterSpacing="0.08em"
                  flexShrink={0}
                >
                  Captura
                </Text>
                {chipsCaptura.map(c => (
                  <TriagemChip
                    key={c.k}
                    c={c}
                    ativo={capturaFiltro === c.k}
                    onClick={() => clicaCaptura(c.k)}
                  />
                ))}
              </Flex>
            )}

            {/* Terceira trilha: panorama por SENTIDO (entrada/saida). Clicável como filtro.
                Contadores vêm do backend (todas as empresas, não só a página). */}
            {conferenciaOk &&
              empresas.panorama?.contadores_sentido &&
              empresas.panorama.contadores_sentido.entrada_erro +
                empresas.panorama.contadores_sentido.entrada_ok +
                empresas.panorama.contadores_sentido.saida_erro +
                empresas.panorama.contadores_sentido.saida_ok >
                0 && (
                <Flex gap={2} wrap="wrap" align="center">
                  <Text
                    fontSize="9.5px"
                    color="#94A3B8"
                    fontWeight={700}
                    textTransform="uppercase"
                    letterSpacing="0.08em"
                    flexShrink={0}
                  >
                    Entrada
                  </Text>
                  <SentidoChip
                    label="Divergência"
                    valor={empresas.panorama.contadores_sentido.entrada_erro}
                    cor="#b91c1c"
                    bg="rgba(239,68,68,0.08)"
                    ativo={sentidoFiltro === 'entrada_erro'}
                    onClick={() =>
                      setSentidoFiltro(f => (f === 'entrada_erro' ? null : 'entrada_erro'))
                    }
                  />
                  <SentidoChip
                    label="OK"
                    valor={empresas.panorama.contadores_sentido.entrada_ok}
                    cor="#047857"
                    bg="rgba(16,185,129,0.08)"
                    ativo={sentidoFiltro === 'entrada_ok'}
                    onClick={() =>
                      setSentidoFiltro(f => (f === 'entrada_ok' ? null : 'entrada_ok'))
                    }
                  />
                  <Text fontSize="9.5px" color="#CBD5E1" mx={1}>
                    |
                  </Text>
                  <Text
                    fontSize="9.5px"
                    color="#94A3B8"
                    fontWeight={700}
                    textTransform="uppercase"
                    letterSpacing="0.08em"
                    flexShrink={0}
                  >
                    Saída
                  </Text>
                  <SentidoChip
                    label="Divergência"
                    valor={empresas.panorama.contadores_sentido.saida_erro}
                    cor="#b91c1c"
                    bg="rgba(239,68,68,0.08)"
                    ativo={sentidoFiltro === 'saida_erro'}
                    onClick={() =>
                      setSentidoFiltro(f => (f === 'saida_erro' ? null : 'saida_erro'))
                    }
                  />
                  <SentidoChip
                    label="OK"
                    valor={empresas.panorama.contadores_sentido.saida_ok}
                    cor="#047857"
                    bg="rgba(16,185,129,0.08)"
                    ativo={sentidoFiltro === 'saida_ok'}
                    onClick={() => setSentidoFiltro(f => (f === 'saida_ok' ? null : 'saida_ok'))}
                  />
                </Flex>
              )}

            {/* Quarta trilha: duplicadas — quantas empresas têm notas duplicadas */}
            {duplicadasQuery.data && duplicadasQuery.data.length > 0 && (
              <Flex gap={2} wrap="wrap" align="center">
                <Text
                  fontSize="9.5px"
                  color="#94A3B8"
                  fontWeight={700}
                  textTransform="uppercase"
                  letterSpacing="0.08em"
                  flexShrink={0}
                >
                  Duplicadas
                </Text>
                <SentidoChip
                  label={`Empresa${duplicadasQuery.data.length > 1 ? 's' : ''}`}
                  valor={duplicadasQuery.data.length}
                  cor="#b45309"
                  bg="rgba(245,158,11,0.08)"
                  ativo={dupFiltro === 'todas'}
                  onClick={() => setDupFiltro(f => (f === 'todas' ? null : 'todas'))}
                />
                <SentidoChip
                  label="Entrada"
                  valor={duplicadasQuery.data.reduce((s, d) => s + d.quantidade_entrada, 0)}
                  cor="#1d4ed8"
                  bg="rgba(59,130,246,0.08)"
                  ativo={dupFiltro === 'entrada'}
                  onClick={() => setDupFiltro(f => (f === 'entrada' ? null : 'entrada'))}
                />
                <SentidoChip
                  label="Saída"
                  valor={duplicadasQuery.data.reduce((s, d) => s + d.quantidade_saida, 0)}
                  cor="#047857"
                  bg="rgba(16,185,129,0.08)"
                  ativo={dupFiltro === 'saida'}
                  onClick={() => setDupFiltro(f => (f === 'saida' ? null : 'saida'))}
                />
              </Flex>
            )}
          </Flex>
          <Flex direction="column" gap={1} maxW={{ base: 'full', md: '360px' }} flex={{ md: 1 }}>
            <Flex justify={{ base: 'flex-start', md: 'flex-end' }} gap={2} align="center">
              {/* Botão de filtros avançados (drawer da casa). Refino ocasional —
                  as trilhas de chips acima continuam sendo a navegação primária. */}
              <Button
                size="sm"
                variant={nAvancados > 0 ? 'solid' : 'outline'}
                colorScheme={nAvancados > 0 ? 'blue' : 'gray'}
                leftIcon={<FunnelSimple size={15} weight={nAvancados > 0 ? 'fill' : 'regular'} />}
                onClick={() => setDrawerFiltros(true)}
                flexShrink={0}
              >
                Filtros{nAvancados > 0 ? ` (${nAvancados})` : ''}
              </Button>
              {/* Excel do recorte atual para a contabilidade: empresas com
                  problema + notas faltantes. Gerado em segundo plano; o aviso
                  de pronta chega no sino. */}
              <Button
                size="sm"
                variant="outline"
                leftIcon={<FileXls size={15} weight="duotone" />}
                onClick={exportarExcel}
                isLoading={exportando}
                loadingText="Iniciando…"
                flexShrink={0}
                title="Gera um Excel com as empresas com problema e as notas faltantes, no recorte atual da tela. O aviso de pronta chega no sino."
              >
                Exportar
              </Button>
              {/* Ordenação: o padrão (mais notas) não prioriza risco. */}
              <MiniToggle<OrdemEmpresas>
                value={ordem}
                onChange={setOrdem}
                options={[
                  ['padrao', 'Padrão'],
                  ['divergencia', 'Nº div.'],
                  ['valor_divergente', 'R$ div.'],
                ]}
              />
              <Flex bg="rgba(15,23,42,0.06)" borderRadius="md" p="2px" gap="2px">
                {(
                  [
                    ['id', 'Id'],
                    ['razao', 'Razão'],
                    ['cnpj', 'CNPJ'],
                  ] as const
                ).map(([m, lbl]) => (
                  <Box
                    key={m}
                    as="button"
                    type="button"
                    px={2}
                    py="1px"
                    borderRadius="sm"
                    fontSize="9px"
                    fontWeight={700}
                    textTransform="uppercase"
                    color={buscaModo === m ? '#0F172A' : '#94A3B8'}
                    bg={buscaModo === m ? 'white' : 'transparent'}
                    boxShadow={buscaModo === m ? 'sm' : 'none'}
                    onClick={() => setBuscaModo(m)}
                  >
                    {lbl}
                  </Box>
                ))}
              </Flex>
            </Flex>
            <InputGroup size="sm">
              <InputLeftElement pointerEvents="none" color="#94A3B8">
                <MagnifyingGlass size={16} />
              </InputLeftElement>
              <Input
                placeholder={
                  {
                    razao: 'Buscar razão social...',
                    cnpj: 'Buscar CNPJ...',
                    id: 'Buscar pelo ID da empresa...',
                  }[buscaModo]
                }
                value={busca}
                onChange={e => setBusca(e.target.value)}
                borderRadius="full"
                bg="rgba(255,255,255,0.7)"
              />
            </InputGroup>
          </Flex>
        </Flex>

        {/* Cicatriz dos filtros avançados: chips removíveis + "N de M" pra nunca
            confundir o recorte com o total. Âmbar de propósito (coça o olho). */}
        {nAvancados > 0 && empresas.data && (
          <Flex
            align="center"
            gap={2}
            wrap="wrap"
            mb={3}
            px={3}
            py={2}
            borderRadius="12px"
            border="1px solid rgba(245,158,11,0.35)"
            bg="rgba(245,158,11,0.07)"
          >
            <Text fontSize="12px" fontWeight={700} color="#b45309" flexShrink={0}>
              {empresas.data.total.toLocaleString('pt-BR')} de{' '}
              {empresas.data.total_periodo.toLocaleString('pt-BR')} empresas
            </Text>
            {descreveAvancados(avancados).map(chip => (
              <Box
                key={chip.k}
                as="button"
                type="button"
                onClick={() => setAvancados(chip.limpa)}
                px="9px"
                py="3px"
                borderRadius="7px"
                fontSize="10.5px"
                fontWeight={700}
                bg="rgba(180,83,9,0.1)"
                color="#b45309"
                _hover={{ bg: 'rgba(180,83,9,0.18)' }}
              >
                {chip.lab} ✕
              </Box>
            ))}
            <Box
              as="button"
              type="button"
              onClick={() => setAvancados({})}
              ml="auto"
              fontSize="11px"
              fontWeight={700}
              color="#b45309"
              textDecoration="underline"
            >
              Limpar
            </Box>
          </Flex>
        )}

        {(vereditoFiltro || capturaFiltro || sentidoFiltro || dupFiltro) && (
          <Flex align="center" gap={2} wrap="wrap" mb={3}>
            <Text fontSize="12px" color="#64748B">
              Filtros combinados:{' '}
              {[
                vereditoFiltro ? `Geral: ${VEREDITO_LAB[vereditoFiltro]}` : '',
                capturaFiltro ? `Captura: ${CAPTURA_LAB[capturaFiltro]}` : '',
                sentidoFiltro
                  ? `${sentidoFiltro.startsWith('saida') ? 'Saídas' : 'Entradas'}: ${sentidoFiltro.endsWith('_ok') ? 'sem divergência' : 'com divergência'}`
                  : '',
                dupFiltro ? `Duplicadas: ${dupFiltro}` : '',
              ]
                .filter(Boolean)
                .join(' + ')}
            </Text>
            <Button size="xs" variant="ghost" onClick={() => clicaTriagem('todas')}>
              Limpar filtros de status
            </Button>
          </Flex>
        )}
        {renderEmpresas()}
      </Box>

      <FiltrosAvancados
        isOpen={drawerFiltros}
        onClose={() => setDrawerFiltros(false)}
        aplicado={avancados}
        ufsDisponiveis={empresas.data?.ufs_disponiveis ?? []}
        onApply={f => {
          setAvancados(f);
          setDrawerFiltros(false);
        }}
        onClear={() => {
          setAvancados({});
          setDrawerFiltros(false);
        }}
      />
      <NotasDrawer empresa={empresaSel} range={range} onClose={() => setEmpresaSel(null)} />
      <AutmaisLogDrawer empresa={logAutmais} range={range} onClose={() => setLogAutmais(null)} />
      <DuplicadasDrawer empresa={dupEmpresa} range={range} onClose={() => setDupEmpresa(null)} />
    </GlassPageBg>
  );
};
